import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

import { RootState } from "../store";

import { APP_LOCAL_KEYS } from "@/app/constants/constants";
import { Action, Permissions } from "@/interfaces/globals";
import { getWorkspace } from "@/utils/storage/appSession";
import { typedLocalStorage } from "@/utils/storage/localStorage";

export type UserPermission = {
  action: string;
  resource: Permissions;
  workspace: string | "*";
};

export type CustomerPermission = Omit<UserPermission, "workspace"> & {
  customer: string;
};

const isMatchingResource = (
  resource: string,
  requiredResource: Permissions,
): boolean => {
  return resource === requiredResource;
};

const getInitialPermissions = (): UserPermission[] => {
  return typedLocalStorage.getJsonOrDefault(
    APP_LOCAL_KEYS.USER_PERMISSIONS,
    [],
  );
};

const initialState: {
  data: UserPermission[];
  customerData: CustomerPermission[];
} = {
  data: getInitialPermissions(),
  customerData: [],
};

export const permissionSlice = createSlice({
  name: "permission",
  initialState,
  reducers: {
    set: (state, action: PayloadAction<UserPermission[]>) => {
      state.data = action.payload;
    },
    setCustomer: (state, action: PayloadAction<CustomerPermission[]>) => {
      state.customerData = action.payload;
    },
    refreshWorkspacePermissions: (state) => {
      state.data = getInitialPermissions();
    },
  },
});

export const { setCustomer, refreshWorkspacePermissions } =
  permissionSlice.actions;

export default permissionSlice.reducer;

export const uPermissions = (state: RootState): UserPermission[] =>
  state.permissions.data;

export const userHasReadAccess = (
  userPermission: UserPermission[] | undefined,
  permissions: Permissions[],
): boolean => {
  const currentWorkspace = getWorkspace();

  if (permissions.length === 0) {
    return true;
  }

  if (!userPermission || userPermission.length === 0) {
    return false;
  }

  if (
    userPermission.find(
      (p) =>
        p.resource === Permissions.ALL &&
        (p.workspace === "*" || p.workspace === currentWorkspace),
    )
  ) {
    return true;
  }

  return permissions.some((permission) =>
    userPermission.find(
      (p) =>
        isMatchingResource(p.resource, permission) &&
        (p.workspace === "*" || p.workspace === currentWorkspace) &&
        p.action === "READ",
    ),
  );
};

// Used in admin panel sidebar to check if user has at least one workspace with read permission, if not then hide the sidebar and show no permission page
export const hasAnyReadPermission = (
  ...requiredPermissions: Permissions[]
): ((x: RootState) => boolean) => {
  return createSelector([uPermissions], (uPermission) => {
    if (requiredPermissions.length === 0) {
      return true;
    }
    if (!uPermission || uPermission.length === 0) {
      return false;
    }
    const readablePermissions = new Set(
      uPermission
        .filter((p) => p.action === Action.READ)
        .map((p) => p.resource),
    );
    if (readablePermissions.has(Permissions.ALL)) {
      return true;
    }
    return requiredPermissions.some((requiredPermission) =>
      readablePermissions.has(requiredPermission),
    );
  });
};

export const hasReadPermission = (
  ...permissions: Permissions[]
): ((x: RootState) => boolean) =>
  createSelector([uPermissions], (userPermission) =>
    userHasReadAccess(userPermission, permissions),
  );

export const hasWritePermission = (
  ...permissions: Permissions[]
): ((x: RootState) => boolean) =>
  createSelector([uPermissions], (uPermission) => {
    const currentWorkspace = getWorkspace();
    if (!uPermission) {
      return false;
    }
    if (uPermission.length === 0) {
      return false;
    }

    if (
      uPermission.find(
        (p) =>
          p.resource === Permissions.ALL &&
          (p.workspace === "*" ||
            p.workspace === currentWorkspace ||
            !currentWorkspace) &&
          p.action === "WRITE",
      )
    ) {
      return true;
    }

    return permissions.some((permission) =>
      uPermission.find(
        (p) =>
          isMatchingResource(p.resource, permission) &&
          (p.workspace === "*" || p.workspace === currentWorkspace) &&
          p.action === "WRITE",
      ),
    );
  });
